import MomentInterval from "./momentinterval";
const moment = window.moment;

// The possible sets of traces to display on the plot
const traceSets = [
  {
    id: "tempDew",
    traces: [
      {
        displayName: "Temperature",
        name: "outTemp"
      },
      {
        displayName: "Dewpoint",
        name: "dewpoint"
      }
    ],
    unit: "°F"
  },
  {
    id: "outHumidity",
    traces: [
      {
        displayName: "Humidity",
        name: "outHumidity"
      }
    ],
    unit: "%"
  },
  {
    id: "barometer",
    traces: [
      {
        displayName: "Pressure",
        name: "barometer"
      }
    ],
    unit: "inHg"
  },
  {
    id: "windChillHeatIndex",
    traces: [
      {
        displayName: "Wind Chill",
        name: "windChill"
      },
      {
        displayName: "Heat Index",
        name: "heatIndex"
      }
    ],
    unit: "°F"
  },
  {
    id: "windSpeed",
    traces: [
      {
        displayName: "Wind Speed",
        name: "windSpeed"
      }
    ],
    unit: "mph"
  },
  {
    id: "windDir",
    traces: [
      {
        displayName: "Wind Direction",
        name: "windDir"
      }
    ],
    unit: "°"
  },
  {
    id: "radiation",
    traces: [
      {
        displayName: "Solar Radiation",
        name: "radiation"
      }
    ],
    unit: "W/m²"
  },
  {
    id: "growingDegreeDays",
    traces: [
      {
        displayName: "Growing Degree Days"
      }
    ],
    unit: "°F-Days"
  }
];

function getTraceSet(id) {
  return traceSets.filter(traceSet => traceSet.id === id)[0];
}

// Allows the user to select which traces to plot
export default class TraceSelect {
  /**
   * @param {Node} root - The DOM node controls will be appended to
   * @param {Plot} plot - The plot traces are added to/removed from
   */
  constructor(root, plot) {
    this._plot = plot;
    this._timeZone = plot.getInterval().start.tz();
    const select = document.createElement("select");
    root.appendChild(select);
    select.addEventListener(
      "change",
      () => {
        this._traceSet = getTraceSet(select.value);
        this._updateTraces();
      },
      false
    );

    for (const traceSet of traceSets) {
      const option = document.createElement("option");
      select.appendChild(option);
      option.setAttribute("value", traceSet.id);
      const text = traceSet.traces.map(trace => trace.displayName).join("/");
      option.innerText = text;
    }

    this._minMaxControl = document.createElement("label");
    root.appendChild(this._minMaxControl);
    this._minMaxControl.innerText = "Min/Max";
    const minMaxCheckbox = document.createElement("input");
    this._minMaxControl.appendChild(minMaxCheckbox);
    minMaxCheckbox.setAttribute("type", "checkbox");
    minMaxCheckbox.addEventListener(
      "change",
      () => {
        this._showMinMax = minMaxCheckbox.checked;
        this._updateTraces();
      },
      false
    );

    this._traceSet = getTraceSet(select.value);
    this._showMinMax = minMaxCheckbox.checked;
    this._updateTraces();
  }

  // Adds/removes traces from the plot based on the state of the controls
  _updateTraces() {
    const dataParams = {
      archiveIntervalMinutes: 5,
      minDataPoints: 25,
      url: null
    };
    const colors = ["steelblue", "crimson"];
    let color_index = 0;
    this._plot.removeTraces();
    this._plot.setYLabel(this._traceSet.unit);
    const aggregateTypes = this._showMinMax ? ["max", "avg", "min"] : ["avg"];
    this._plot.loadTracesAndRedraw();
    if (this._traceSet.id === "growingDegreeDays") {
      this._minMaxControl.style.display = "none";
      var ONE_DAY_IN_MIN = 24 * 60;
      var gddDataParams = {
        url: "/weewx/gdd_binding/cumuGrowingDegreeDays",
        archiveIntervalMinutes: ONE_DAY_IN_MIN,
        aggregateType: "avg",
        minDataPoints: 25
      };
      const colors = [
        "blue",
        "green",
        "red",
        "yellow",
        "black",
        "purple",
        "fuchsia",
        "aqua",
        "lime",
        "silver",
        "maroon"
      ];
      const firstYear = 2015;
      const currentYear = new Date().getFullYear();
      const ONE_YEAR_IN_MS = 1e3 * 60 * ONE_DAY_IN_MIN * 365.25;
      for (let year = firstYear; year <= currentYear; year++) {
        const yearsBeforeCurrent = currentYear - year;
        gddDataParams.offset = ONE_YEAR_IN_MS * yearsBeforeCurrent;
        this._plot.addTrace(
          `${year}`,
          "",
          gddDataParams,
          colors[yearsBeforeCurrent],
          [],
          1.5
        );
      }
      var gddMaxInterval = new MomentInterval(
        moment.tz(new Date(`4/1/${currentYear}`), this._timeZone),
        moment.tz(new Date(`10/31/${currentYear}`), this._timeZone)
      );
      this._plot.setMaxInterval(gddMaxInterval);
      this._plot.setInterval(gddMaxInterval);
      var ONE_DAY_IN_MS = ONE_DAY_IN_MIN * 60 * 1e3;
      this._plot.setMinIntervalLength(ONE_DAY_IN_MS);
    } else {
      if (this._prevTraceSet && this._prevTraceSet.id === "growingDegreeDays") {
        var maxInterval = new MomentInterval(
          moment.tz(new Date("1/1/2015"), this._timeZone),
          moment.tz(Date.now(), this._timeZone)
        );
        this._minMaxControl.style.removeProperty("display");
        this._plot.setMaxInterval(maxInterval);
        var ONE_HOUR_IN_MS = 60 * 60 * 1e3;
        this._plot.setMinIntervalLength(ONE_HOUR_IN_MS);
        this._plot.setInterval(maxInterval);
      }
      for (const trace of this._traceSet.traces) {
        for (const aggType of aggregateTypes) {
          const URLbase = "/weewx/wxplot_binding/";
          if (this._traceSet.id === "windSpeed" && aggType === "max") {
            dataParams.url = URLbase + "windGust";
          } else {
            dataParams.url = URLbase + trace.name;
          }
          dataParams.aggregateType = aggType;
          const traceOptions = {};
          this._plot.addTrace(
            aggType,
            trace.displayName,
            dataParams,
            colors[color_index],
            [],
            aggType === "avg" ? 1.5 : 0.5,
            traceOptions
          );
        }
        color_index++;
      }
    }
    this._plot.loadTracesAndRedraw();
    this._prevTraceSet = this._traceSet;
  }
}
